import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

export interface EntityInspectorFallback {
  label?: string;
  status?: string;
  type?: string;
}

interface EntityInspectorContextValue {
  open: boolean;
  entityId: string | null;
  projectId: string | null;
  fallback?: EntityInspectorFallback;
  openInspector: (
    entityId: string,
    projectId: string,
    fallback?: EntityInspectorFallback,
  ) => void;
  navigateInspector: (entityId: string, fallback?: EntityInspectorFallback) => void;
  closeInspector: () => void;
}

const EntityInspectorContext = createContext<EntityInspectorContextValue | null>(null);

export const EntityInspectorProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [entityId, setEntityId] = useState<string | null>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [fallback, setFallback] = useState<EntityInspectorFallback | undefined>();

  const openInspector = useCallback(
    (id: string, pid: string, fb?: EntityInspectorFallback) => {
      setEntityId(id);
      setProjectId(pid);
      setFallback(fb);
      setOpen(true);
    },
    [],
  );

  const navigateInspector = useCallback((id: string, fb?: EntityInspectorFallback) => {
    setEntityId(id);
    setFallback(fb);
  }, []);

  const closeInspector = useCallback(() => {
    setOpen(false);
  }, []);

  const value = useMemo(
    () => ({
      open,
      entityId,
      projectId,
      fallback,
      openInspector,
      navigateInspector,
      closeInspector,
    }),
    [open, entityId, projectId, fallback, openInspector, navigateInspector, closeInspector],
  );

  return (
    <EntityInspectorContext.Provider value={value}>{children}</EntityInspectorContext.Provider>
  );
};

export function useEntityInspector(): EntityInspectorContextValue {
  const ctx = useContext(EntityInspectorContext);
  if (!ctx) {
    throw new Error('useEntityInspector must be used within EntityInspectorProvider');
  }
  return ctx;
}
